// Módulo base de Valorización

import { useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';
import { useCompanyCore } from '../company';
import { valorizationSelector, loadingSelector, errorCodeSelector } from './store/selector';
import { valorizationApiService } from './services/api';
import { ValorizationData } from './interfaces/valorization.interface';
import { PostValorizationPayloadRequest } from './interfaces/post-valorization';
import {
  getValorizationInitApiAction,
  getValorizationSuccessApiAction,
  getValorizationFailureApiAction,
  postValorizationInitApiAction,
  postValorizationSuccessApiAction,
  postValorizationFailureApiAction,
  clearValorizationAction,
} from './store/reducer';

export function useValorizationCore() {
  const valorization = useSelector(valorizationSelector);
  const loading = useSelector(loadingSelector);
  const errorCode = useSelector(errorCodeSelector);
  const { companyId } = useCompanyCore();
  const dispatch = useDispatch();

  const getValorization = useCallback(
    async (
      articleId: number,
      onSuccess?: (valorization: ValorizationData) => void,
      onFailure?: () => void
    ) => {
      if (!companyId) return;

      try {
        dispatch(getValorizationInitApiAction());
        const response = await valorizationApiService.getValorization(companyId, articleId);
        dispatch(getValorizationSuccessApiAction(response));
        onSuccess?.(response);
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(
          getValorizationFailureApiAction({ errorCode: error.request?.status || 500 })
        );
      }
    },
    [companyId, dispatch]
  );

  const postValorization = useCallback(
    async (
      articleId: number,
      body: PostValorizationPayloadRequest,
      onSuccess?: (valorization: ValorizationData) => void,
      onFailure?: () => void
    ) => {
      if (!companyId) return;

      try {
        dispatch(postValorizationInitApiAction());
        
        // Usar el endpoint correcto para actualizar el precio del producto
        const response = await valorizationApiService.updateProductPrice(
          companyId, 
          articleId, 
          body.market_price
        );
        
        // Crear una respuesta simulada para mantener compatibilidad con el estado
        const mockResponse: ValorizationData = {
          id: 0,
          article_id: articleId,
          market_price: body.market_price,
          suggested_price: body.suggested_price || 0,
          price_difference: 0,
          price_difference_percentage: 0,
          notes: body.notes || '',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          created_by: 0,
          updated_by: 0,
        };
        
        dispatch(postValorizationSuccessApiAction(mockResponse));
        onSuccess?.(mockResponse);
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(
          postValorizationFailureApiAction({ errorCode: error.request?.status || 500 })
        );
      }
    },
    [companyId, dispatch]
  );

  const clearValorization = useCallback(() => {
    dispatch(clearValorizationAction());
  }, [dispatch]);

  return {
    valorization,
    loading,
    errorCode,
    getValorization,
    postValorization,
    clearValorization,
  };
} 